import React, { useState, useCallback } from 'react';
import { Card, Modal, Descriptions, Input } from 'antd';
import { EyeOutlined, MessageOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { SearchBar } from '@/components/SearchBar';
import { createTimeColumn, createLongTextColumn } from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

type ContactRow = Record<string, unknown> & {
  id: number | string;
  name?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  company?: string;
  message?: string;
  createTime?: number | string;
};

/** 从详情对象中安全解析提交时间（支持 createTime / create_time，数字秒/毫秒、ISO 字符串） */
function formatSubmissionTime(row: ContactRow): string {
  const raw =
    (row.createTime as number | string | undefined) ??
    (row as Record<string, unknown>).create_time as number | string | undefined;
  if (raw == null || raw === '') return '-';
  try {
    let date: Date;
    if (typeof raw === 'number') {
      date = raw < 1e12 ? new Date(raw * 1000) : new Date(raw);
    } else {
      date = new Date(raw);
    }
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN');
  } catch {
    return '-';
  }
}

const normalizeRows = (rows: unknown[]): ContactRow[] => {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    return {
      ...item,
      ...data,
      id: (data.id as number) ?? (item.id as number) ?? (data.id as string) ?? (item.id as string),
    } as ContactRow;
  });
};

const ContactSubmissionList: React.FC = () => {
  const fetchApi = async (pn: number, rn: number, custom?: string) => {
    const res = await Service.ycContactSubmissionControllerGetList(pn, rn, custom);
    const raw = (res as { data?: { rows?: unknown[]; count?: number; pn?: number } })?.data;
    const rows = raw?.rows ?? [];
    return {
      errNo: (res as { errNo?: number }).errNo ?? 0,
      data: {
        rows: normalizeRows(rows),
        count: raw?.count ?? 0,
        pn: raw?.pn ?? pn,
      },
    };
  };

  const {
    data: list,
    loading,
    pagination,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<ContactRow>({
    fetchData: fetchApi,
    errorMessage: '获取联系列表失败',
    searchOptions: { fields: ['name', 'email', 'message', 'company'] },
  });

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ContactRow | null>(null);

  const loadDetail = useCallback(async (id: string | number) => {
    setDetailLoading(true);
    try {
      const res = await Service.ycContactSubmissionControllerGetDetail(String(id));
      const d = (res as { data?: Record<string, unknown> })?.data;
      const raw = (d?.data as Record<string, unknown>) || d;
      setDetail({ ...raw, id } as ContactRow);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleView = useCallback(
    (record: ContactRow) => {
      setDetailVisible(true);
      setDetail(record);
      loadDetail(record.id);
    },
    [loadDetail]
  );

  const columns: ColumnsType<ContactRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 160 },
    { title: '电话', dataIndex: 'phone', key: 'phone', width: 120 },
    createLongTextColumn('公司', 'company', 12),
    createLongTextColumn('留言', 'message', 20),
    createTimeColumn('full'),
  ];

  const stats: DataStat[] = [
    {
      title: '联系总数',
      value: pagination.total,
      prefix: <MessageOutlined />,
      valueStyle: { color: '#3f8600' },
    },
    { title: '当前页', value: list.length, valueStyle: { color: '#1890ff' } },
  ];

  const actionButtons: ActionButton<ContactRow>[] = [
    { key: 'view', label: '查看', icon: <EyeOutlined />, onClick: handleView },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats stats={stats} className={styles.statsRow} />
        <div className={styles.toolbar}>
          <SearchBar
            search={search}
            placeholder="搜索姓名、邮箱、留言、公司"
            width={300}
            loading={loading}
          />
        </div>
        <DataTable
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>
      <Modal
        title="联系详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>加载中...</div>
        ) : detail ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="姓名">{detail.name ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{detail.email ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="电话">{detail.phone ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="WhatsApp">{detail.whatsapp ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="公司">{detail.company ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="留言">
              <Input.TextArea
                readOnly
                value={detail.message ?? ''}
                rows={4}
                style={{ resize: 'none', border: 'none', background: 'transparent' }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="提交时间">
              {formatSubmissionTime(detail)}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </div>
  );
};

export default ContactSubmissionList;
